import { createContext, useContext } from "react";
import type {
    CopyableField,
    QueueDetails,
    QueueState,
    QueueSummary,
    RecoveredDraft,
    RejectedFile,
} from "./uploadQueue";

/** The bulk uploader's route — the Library links here and the floating indicator hides on it. */
export const BULK_UPLOAD_PATH = "/library/upload";

/**
 * How many files transfer at once. Each upload already sends its parts one after another, so this
 * is the whole of the parallelism: enough to keep a fast connection busy, few enough that one slow
 * file does not starve the rest and a forty-file drop does not open forty connections.
 */
export const MAX_CONCURRENT_UPLOADS = 3;

/** How often processing status is checked. A transcode takes minutes; seconds would be noise. */
export const POLL_INTERVAL_MS = 5000;

export interface UploadQueueContextValue {
    state: QueueState;
    summary: QueueSummary;
    /** Queues what it can and reports what it refused, so the caller can say why. */
    addFiles: (files: readonly File[], organizationId: string | undefined) => RejectedFile[];
    recover: (drafts: RecoveredDraft[]) => void;
    retry: (key: string) => void;
    /** Stops a transfer, discards an unfinished draft on the server, or just clears a finished row. */
    remove: (key: string) => Promise<void>;
    editDetails: (key: string, patch: Partial<QueueDetails>) => void;
    copyDetails: (fromKey: string, fields: CopyableField[]) => void;
    /** Publishes now if the bytes are in, or as soon as they are. */
    finish: (key: string) => void;
    clearFinished: () => void;
}

export const UploadQueueContext = createContext<UploadQueueContextValue | null>(null);

/** The bulk upload queue. Throws outside `UploadQueueProvider`, which sits above the router. */
export const useUploadQueue = (): UploadQueueContextValue => {
    const value = useContext(UploadQueueContext);
    if (!value) throw new Error("useUploadQueue must be used inside UploadQueueProvider");
    return value;
};
